import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ChannelSnapshotStatus } from '@playerbase/types';
import { Alert, Badge, Button, Card, Tooltip } from '@playerbase/ui';

import { SNAPSHOT_COOLDOWN_MS } from './constants.js';
import { requestSnapshotErrorCopy } from './error-copy.js';
import { formatCooldownRemaining, relativeTime } from './format-time.js';
import {
  requestChannelSnapshot,
  type RequestSnapshotError,
} from './insights-data.js';

// Per-watched-channel card. Shows when this channel's insights were last
// refreshed, summarises the last refresh, and offers a Refresh button.
// The button is disabled while a refresh is in flight or while in the
// 30-minute cooldown after a completed refresh.
//
// Naming: deliberately operational ("Refresh insights", "Last refreshed")
// rather than AI-flavoured. Per project memory feedback_hide_ai_layer.

export interface ChannelSnapshotCardProps {
  channel: ChannelSnapshotStatus;
  // The game this card belongs to. Post-0082 request_channel_snapshot
  // takes (channel, game) so the per-channel refresh button needs the
  // game scope explicitly.
  gameId: string;
  // Bumped by the Insights page when refresh completes via realtime, so
  // the cooldown surface re-renders. The component owns its own local
  // tick for cooldown countdown too.
  refreshVersion?: number;
}

export function ChannelSnapshotCard({
  channel,
  gameId,
  refreshVersion,
}: ChannelSnapshotCardProps): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<RequestSnapshotError | null>(null);
  // Local tick so the cooldown text counts down without external help.
  const [, setTick] = useState(0);

  useEffect(() => {
    if (channel.lastDoneAt === null) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 30_000);
    return () => window.clearInterval(id);
  }, [channel.lastDoneAt]);

  const cooldown = useMemo(() => {
    if (channel.lastDoneAt === null) return { active: false, remainingMs: 0 };
    const deadline = new Date(channel.lastDoneAt).getTime() + SNAPSHOT_COOLDOWN_MS;
    const remaining = deadline - Date.now();
    // TEMPORARY: cooldown bypass while we exercise the snapshot worker for
    // the Insights MVP (mirrors migration 0077). Revert by changing `active`
    // back to `remaining > 0`.
    return { active: false, remainingMs: remaining };
  }, [channel.lastDoneAt, refreshVersion]);

  const disabled = submitting || channel.inFlight || cooldown.active;
  const buttonLabel = submitting
    ? 'Refreshing…'
    : channel.inFlight
      ? 'Refreshing…'
      : 'Refresh insights';

  const handleRefresh = useCallback(async (): Promise<void> => {
    setError(null);
    setSubmitting(true);
    try {
      const result = await requestChannelSnapshot(
        channel.discordChannelId,
        gameId,
      );
      if (!result.ok) {
        setError(result.error ?? 'unknown');
      }
    } finally {
      setSubmitting(false);
    }
  }, [channel.discordChannelId, gameId]);

  const heading = channel.channelName !== null
    ? `# ${channel.channelName}`
    : `# ${channel.discordChannelId}`;

  const tooltipContent = cooldown.active
    ? `Refreshed recently — ${formatCooldownRemaining(cooldown.remainingMs)}.`
    : channel.inFlight
      ? 'Refresh in progress.'
      : null;

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between gap-2">
          <Card.Title>{heading}</Card.Title>
          {channel.inFlight && (
            <Badge tone="info">In progress</Badge>
          )}
        </div>
        <Card.Description>
          {channel.lastDoneAt !== null
            ? `Last refreshed ${relativeTime(channel.lastDoneAt)}`
            : 'Awaiting first refresh.'}
        </Card.Description>
      </Card.Header>

      {channel.lastDoneAt !== null && (
        <Card.Content>
          <p className="text-sm text-text-muted">
            {channel.attachedCount !== null && channel.attachedCount > 0
              ? `${channel.attachedCount} theme${channel.attachedCount === 1 ? '' : 's'} touched`
              : 'No themes touched yet'}
            {channel.newThemeCount !== null && channel.newThemeCount > 0
              ? ` · ${channel.newThemeCount} new theme${channel.newThemeCount === 1 ? '' : 's'}`
              : ''}
          </p>
        </Card.Content>
      )}

      <Card.Footer>
        <div className="flex w-full flex-col gap-2">
          <div className="flex justify-end">
            <Tooltip content={tooltipContent}>
              <Button
                onClick={() => void handleRefresh()}
                disabled={disabled}
              >
                {buttonLabel}
              </Button>
            </Tooltip>
          </div>
          {error !== null && (
            <Alert tone="danger" role="alert">{requestSnapshotErrorCopy(error)}</Alert>
          )}
        </div>
      </Card.Footer>
    </Card>
  );
}
